import { type MessageDraftV2 } from '@pubnub/chat';
import {
  type ChangeEventHandler,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useVoiceRecorder } from '@avanta/shared/hooks/Chat/useVoiceRecorder';
import { useIsMounted } from '@avanta/shared/hooks/useIsMounted';
import {
  useActiveConversation,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { usePredefinedPhrasesStore } from '@avanta/shared/stores/chat/predefined-phrases';
import { cloneFilesForPubNub } from '@avanta/shared/utils/chat';
import { getUserId } from '@avanta/shared/utils/storage';
import { stripHtmlTags, toRichTextParagraph } from '@avanta/shared/utils/text';
import { type ChatInputFormValues, type InputChatProps, type InputChatSendPayload } from '../types';

interface UseChatInputOptions {
  onAfterSend?: InputChatProps['onAfterSend'];
}

interface UseChatInputResult {
  methods: UseFormReturn<ChatInputFormValues>;
  selectedFiles: File[];
  fileInputRef: RefObject<HTMLInputElement | null>;
  isSending: boolean;
  isRecording: boolean;
  audioFile: File | null;
  recordingTime: string;
  handleSendMessage: (data: ChatInputFormValues) => Promise<void>;
  handleMessageChange: (value: string) => void;
  handleFileChange: ChangeEventHandler<HTMLInputElement>;
  handleRemoveFile: (lastModified: number) => void;
  handleStartRecording: () => void;
  handleAttachClick: () => void;
}

export const useChatInput = ({ onAfterSend }: UseChatInputOptions = {}): UseChatInputResult => {
  const activeConversation = useActiveConversation();
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const { sendTextMessage } = useMessagesStore();
  const methods = useForm<ChatInputFormValues>({
    mode: 'onChange',
    defaultValues: { message: '' },
  });
  const { reset, setValue } = methods;
  const [draft, setDraft] = useState<MessageDraftV2 | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSending, setIsSending] = useState(false);
  const { isRecording, startRecording, stopRecording, audioFile, recordingTime, resetRecording } =
    useVoiceRecorder();
  const isMounted = useIsMounted();
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const setPhraseHandler = usePredefinedPhrasesStore(state => state.setPhraseHandler);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const conversationRef = useRef(activeConversation);
  conversationRef.current = activeConversation;

  useEffect(() => {
    const channel = activeConversation?.channel;
    if (!activeChannelId || !channel) return;

    const newDraft = channel.createMessageDraftV2({
      isTypingIndicatorTriggered: true,
    });
    setDraft(newDraft);
    reset();
    return (): void => {
      setDraft(null);
    };
  }, [activeChannelId, activeConversation?.channel, reset]);

  useEffect(() => {
    setPhraseHandler((phrase: string): void => {
      const currentDraft = draftRef.current;
      const currentConversation = conversationRef.current;
      if (!currentDraft) return;

      const htmlContent = toRichTextParagraph(phrase);
      setValue('message', htmlContent, { shouldDirty: true, shouldValidate: true });
      currentDraft.update(phrase);

      if (currentConversation?.channel && phrase.trim()) {
        currentConversation.channel.startTyping();
      }
    });

    return (): void => setPhraseHandler(null);
  }, [setPhraseHandler, setValue]);

  const handleSendMessage = useCallback(
    async (data: ChatInputFormValues): Promise<void> => {
      const htmlContent = data.message;
      const plainText = stripHtmlTags(htmlContent);
      const hasContent = Boolean(plainText) || selectedFiles.length > 0 || audioFile !== null;

      if (!hasContent || !isMounted() || !activeChannelId) return;

      try {
        setIsSending(true);
        const channel = activeConversation?.channel;

        if (channel) {
          await channel.stopTyping();
        }

        const sentFiles = selectedFiles;
        const sentAudio = audioFile;
        const params: InputChatSendPayload = {
          sessionId: activeChannelId,
          senderId: getUserId(),
          receiverId: activeConversation?.otherUser.id || '',
          hasText: Boolean(plainText),
          files: sentFiles,
          audioFile: sentAudio,
        };

        if (selectedFiles.length > 0 || audioFile) {
          if (!draft) {
            toast.error(t('Chat.Messages.send.error'));
            return;
          }
          if (selectedFiles.length > 0) {
            draft.files = cloneFilesForPubNub(selectedFiles);
          }
          if (audioFile) {
            draft.files = cloneFilesForPubNub([audioFile]);
            resetRecording();
          }
          await draft.send();
        }
        if (plainText) {
          await sendTextMessage(activeChannelId, htmlContent);
        }

        if (draft) draft.update('');
        reset();
        setSelectedFiles([]);
        await onAfterSend?.(params);
      } catch {
        toast.error(t('Chat.Messages.send.error'));
      } finally {
        setIsSending(false);
      }
    },
    [
      activeChannelId,
      activeConversation,
      audioFile,
      draft,
      isMounted,
      onAfterSend,
      reset,
      resetRecording,
      selectedFiles,
      sendTextMessage,
      t,
    ]
  );

  const handleMessageChange = useCallback(
    (value: string): void => {
      const plainText = stripHtmlTags(value);
      if (activeConversation?.channel && plainText.trim()) {
        activeConversation.channel.startTyping();
      }
      if (draft) {
        draft.update(plainText);
      }
    },
    [activeConversation, draft]
  );

  const handleFileChange = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
    const files = event.target.files;
    if (!files) return;
    setSelectedFiles(previousFiles => [...previousFiles, ...Array.from(files)]);
  }, []);

  const handleRemoveFile = useCallback(
    (lastModified: number): void => {
      setSelectedFiles(previousFiles =>
        previousFiles.filter(file => file.lastModified !== lastModified)
      );
      if (audioFile?.lastModified === lastModified) {
        resetRecording();
      }
    },
    [audioFile, resetRecording]
  );

  const handleStartRecording = useCallback((): void => {
    if (isRecording) {
      void stopRecording();
      return;
    }
    void startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const handleAttachClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  return {
    methods,
    selectedFiles,
    fileInputRef,
    isSending,
    isRecording,
    audioFile,
    recordingTime,
    handleSendMessage,
    handleMessageChange,
    handleFileChange,
    handleRemoveFile,
    handleStartRecording,
    handleAttachClick,
  };
};
